/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';

// the optional Tab Indicator patterns from the DLS "Tabs" spec (Tab w/Status
// Dot, Tab w/Badge, Tab w/Urgent Badge). Annotation is a documented pattern
// too, but its spec isn't final yet - it's intentionally not implemented ...
export enum TabIndicatorVariant {
  statusDot = 'statusDot',
  badge = 'badge',
  urgentBadge = 'urgentBadge'
};

export interface TabIndicatorProps {
  variant: TabIndicatorVariant | `${TabIndicatorVariant}`;
  // required for badge/urgentBadge, ignored by statusDot ...
  count?: number;
  // accessible description for the indicator (e.g. "3 new notifications") -
  // the status dot has no visible text, so without it it's purely visual ...
  ariaLabel?: string;
};

// Figma: 19px circle + a 1px centered stroke of the same color = 20px outer,
// Inter 600 10px count. minWidth (not a fixed width) lets 3+ digit counts
// grow into a pill instead of overflowing the circle ...
const badgeBase = {
  minWidth       : '20px',
  height         : '20px',
  padding        : '0 3px',
  boxSizing      : 'border-box',
  display        : 'inline-flex',
  flexShrink     : 0,
  alignItems     : 'center',
  justifyContent : 'center',
  borderRadius   : '100px',
  fontFamily     : 'Inter',
  fontSize       : '10px',
  fontWeight     : 600,
  lineHeight     : '12px',
  letterSpacing  : '0px'
} as const;

const useStyles = makeStyles(() => ({
  // Figma "12px/StatusIndicator/Stroke/Issue": 10px red fill inside a 1px
  // white ring (12px outer) ...
  statusDot : {
    width           : '12px',
    height          : '12px',
    boxSizing       : 'border-box',
    display         : 'inline-block',
    flexShrink      : 0,
    borderRadius    : '50%',
    backgroundColor : dlsColor('red'),
    border          : `1px solid ${dlsColor('white')}`
  },
  badge : {
    ...badgeBase,
    color           : dlsColor('grey1'),
    backgroundColor : dlsColor('grey5'),
    border          : `1px solid ${dlsColor('grey5')}`
  },
  urgentBadge : {
    ...badgeBase,
    color           : dlsColor('white'),
    backgroundColor : dlsColor('red'),
    border          : `1px solid ${dlsColor('red')}`
  }
}));

/**
 * Renders one of the DLS Tab Indicator patterns next to a tab label.
 *
 * ```typescript
 *
 *  import {TabIndicator} from '@ion/dls/src/components/tabs/current/TabIndicator';
 *
 *  <TabIndicator variant='statusDot' ariaLabel='Has issues' />
 *  <TabIndicator variant='badge' count={33} />
 *  <TabIndicator variant='urgentBadge' count={16} />
 *
 *  // or through a tab item (current theme only) ...
 *  <TabView tabItems={[{tabHeader: 'Assets', indicator: {variant: 'badge', count: 33}}]} />
 *
 * ```
 */
export const TabIndicator = ({
  variant,
  count,
  ariaLabel
}: TabIndicatorProps) => {
  const classes = useStyles();

  if (variant === TabIndicatorVariant.statusDot) {
    return (
      <span className={classes.statusDot}
        data-testid='tab-indicator-status-dot'
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true} />
    );
  }

  if (typeof count !== 'number') {
    return null;
  }

  return (
    <span className={variant === TabIndicatorVariant.urgentBadge ? classes.urgentBadge : classes.badge}
      data-testid={`tab-indicator-${variant === TabIndicatorVariant.urgentBadge ? 'urgent-badge' : 'badge'}`}
      aria-label={ariaLabel}>
      {count}
    </span>
  );
};

export default memo(TabIndicator);
